import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '@app/components/base/base.component';
import { MenusServices } from '@app/services/menus.service';
import { NotificationService } from '@app/services/notifications.service';
import {
  CustomerFilterType,
  customerTypes,
  queryParamsQuote,
  isOpenQuoteBlockingStandardEdit,
  isOpenQuoteFullyTerminal,
  isOpenQuoteInReview,
  openQuoteStatusDisplayLabel,
  openQuoteStatusListCssClass,
  OPEN_QUOTE_STATUS_DIRO_OPTIONS,
} from '../model/open-quote.model';
import { AuthService } from '@app/common/auth.service';
import { ModalService } from '@app/components/modal/modal.service';
import { OpenQuoteService } from '../services/open-quote-service.service';
import { Util } from '@app/core/util/Util';
import { ResponsiveService } from '@app/services/responsive.service';
import { environment } from '@env/environment.local';
import { CLIENT_SERVICE } from '@app/pages/clients/interfaces/endpoints';
import { createLoadingTracker } from '@app/core/util/loading-tracker';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';

// Cambio para merge
@Component({
  template: '',
})
export class ListOpenQuoteWrapper extends BaseComponent implements OnInit {
  @ViewChild('templateConfirmation') template!: TemplateRef<any>;
  readonly templateDuplicateQuote = viewChild.required<TemplateRef<any>>(
    'templateDuplicateQuote'
  );
  readonly responsive = inject(ResponsiveService);
  private readonly routerNavigate = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly authService = inject(AuthService);
  private readonly quoteService = inject(OpenQuoteService);

  readonly loading = createLoadingTracker();
  readonly isLoading = computed(() => this.loading.count() > 0);
  openRowOption = signal<string>('');
  hasData: boolean = false;
  data: any[] = [];
  total: number = 0;
  pageSize: number = 20;
  pageIndex: number = 1;
  module: string = '';
  reset: boolean = false;
  owners: any[] = [];
  selectedOwner: any;
  userId: string;
  idQuote: number;

  extendingQuoteId: number | null = null;
  readonly showQuoteValidityListFilter = environment.showQuoteValidityListFilter;
  title: string = 'Eliminar cotización';
  subtitle: string = '';
  btn1: string = 'Cancelar';
  btn2: string = 'Eliminar';
  customerTypeList: any[] = customerTypes;
  isActiveClientCatalog = environment.active_client_catalog;

  params: queryParamsQuote = {
    filter: {
      page: this.pageIndex,
      pageSize: this.pageSize,
      me: false,
      customerName: null,
      userId: null,
      quoteName: null,
      status: null,
      from: null,
      to: null,
      customerId: null,
      isExternalCustomer: null,
    },
  };

  readonly readonlyAdditionalSearchClientParams = {
    rfc: null,
    personType: null,
    page: 1,
    pageSize: 30,
  }

  status: any = {
    data: [...OPEN_QUOTE_STATUS_DIRO_OPTIONS],
    placeholder: 'Buscar por estado',
    filterType: 'status',
  };

  searchNameQuote: any = { placeholder: 'Buscar cotización', value: '' };
  searchNameCustomer: any = { placeholder: 'Buscar cliente', value: '' };

  constructor(
    private router: ActivatedRoute,
    private menu: MenusServices,
    notification: NotificationService,
  ) {
    super(router, menu, notification);
    this.module = this.sendDataToMenu();
    effect(
      () => {
        if (this.responsive.isMobileSignal()) {
          this.closeMobileMenuOptions();
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit() {
    this.getQuotes();
    this.getOwners();
    this.userId = this.authService.currentUserIds;
    this.responsive.onListen();
  }

  getRowKey(id: string, i: number) {
    return `${id}-${i}`;
  }

  isMenuRowOpen(id: string) {
    return this.openRowOption() === id;
  }

  toggleMobileMenuOptions(e: MouseEvent, updated: string) {
    e.preventDefault();
    this.openRowOption.update((value) => (value !== '' ? '' : updated));
  }

  closeMobileMenuOptions() {
    this.openRowOption.set('');
  }

  getQuotes() {
    this.loading.track(this.quoteService.getListQuotesOpen(this.params)).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (response) => {
        this.data = response.data;
        this.total = response.totalElements;
        this.pageIndex = response.page - 1;
        this.hasData = this.data.length !== 0;
      },
      error: (error) => {
        this.showNotificationError('Error al obtener las cotizaciones');
      },
    });
  }

  handlePage(e: any) {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.params.filter.page = e.pageIndex + 1;
    this.params.filter.pageSize = e.pageSize;
    this.getQuotes();
  }

  deleteQuoteById(): void {
    this.closeModal();
    if (this.data?.some((q: any) => q?.id === this.idQuote && q?.linkedToCreditRequest)) {
      this.showNotificationError('Esta cotización está vinculada a una solicitud de crédito y no se puede editar.');
      return;
    }
    this.loading.track(this.quoteService.deleteQuoteOpen(this.idQuote)).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: () => {
        this.showNotificationSuccess(
          'La cotización ha sido eliminada con éxito.'
        );
        this.getQuotes();
      },
      error: () => {
        this.showNotificationError(
          'Ha ocurrido un error, la cotización no se pudo eliminar.'
        );
      },
    });
  }

  duplicateQuoteById(): void {
    this.closeModal();
    this.loading.track(this.quoteService.duplicateOpenQuote(this.idQuote)).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (response: any) => {
        this.showNotificationSuccess(
          'La cotización ha sido duplicada con éxito.'
        );
        this.routerNavigate.navigate([
          '/quote/edit-quote-open/' + response.data.id,
        ]);
        // this.getQuotes();
      },
      error: () => {
        this.showNotificationError(
          'Ha ocurrido un error, la cotización no se pudo duplicar.'
        );
      },
    });
  }

  closeModal() {
    this.modalService.closeModal();
  }

  canExtendOpenQuoteValidity(quote: {
    valid?: boolean;
    minQuoteValidityDays?: number;
    userId?: string | number;
    validityExtensionApplied?: boolean;
    hasInactiveFinancialSetup?: boolean;
    linkedToCreditRequest?: boolean | null;
  }): boolean {
    return (
      this.showQuoteValidityListFilter &&
      quote?.valid === false &&
      quote?.minQuoteValidityDays != null &&
      quote?.validityExtensionApplied !== true &&
      quote?.hasInactiveFinancialSetup !== true &&
      quote?.linkedToCreditRequest !== true &&
      String(this.userId) === String(quote.userId)
    );
  }

  extendOpenQuoteValidity(quote: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.extendingQuoteId != null) {
      return;
    }
    this.extendingQuoteId = quote.id;
    this.quoteService.extendOpenQuoteValidity(quote.id).pipe(
      finalize(() => {
        this.extendingQuoteId = null;
      })
    ).subscribe({
      next: (response) => {
        if (response?.data) {
          quote.validityEndDate = response.data.validityEndDate;
          quote.valid = response.data.valid;
          quote.minQuoteValidityDays = response.data.minQuoteValidityDays;
          quote.validityExtensionApplied = response.data.validityExtensionApplied ?? true;
          quote.hasInactiveFinancialSetup = response.data.hasInactiveFinancialSetup;
        }
        this.showNotificationSuccess('Vigencia extendida correctamente.');
      },
      error: (error) => {
        const msg =
          error?.error?.errors?.[0] ??
          'No se pudo extender la vigencia de la cotización.';
        this.showNotificationError(typeof msg === 'string' ? msg : String(msg));
      },
    });
  }

  getMobileMenuTriggerId(id: string) {
    return `menu-${id}`;
  }

  quoteIsTerminalCustomerOnly(quote: { status?: string }): boolean {
    return isOpenQuoteFullyTerminal(quote?.status);
  }

  quoteIsInReview(quote: { status?: string }): boolean {
    return isOpenQuoteInReview(quote?.status);
  }

  /** Completado / Aprobado / En revisión: sin Editar ni Eliminar. */
  quoteBlocksEditAndDelete(quote: { status?: string }): boolean {
    return isOpenQuoteBlockingStandardEdit(quote?.status);
  }

  getStatusClass(status: string | null | undefined) {
    return openQuoteStatusListCssClass(status);
  }

  getStatus(status: string | null | undefined) {
    return openQuoteStatusDisplayLabel(status);
  }

  search() {
    this.params.filter.page = 1;
    this.getQuotes();
  }

  filter(value: any, typeSelect: string) {
    switch (typeSelect) {
      case 'status':
        this.params.filter.status = value;
        break;
      case 'quoteName':
        this.params.filter.quoteName = value === '' ? null : value;
        break;
      case 'customerName':
        this.params.filter.customerName = value === '' ? null : value;
        break;
      case 'userId':
        this.params.filter.userId = value === '' ? null : value;
        break;
      case 'me':
        this.params.filter.me = value;
        this.search();
        break;
      case 'from':
        if (value) {
          const d = new Date(value);
          d.setHours(0, 0, 0, 0);
          this.params.filter.from = d;
        } else {
          this.params.filter.from = null;
        }
        break;
      case 'to':
        if (value) {
          const d = new Date(value);
          d.setHours(23, 59, 59, 999);
          this.params.filter.to = d;
        } else {
          this.params.filter.to = null;
        }
        break;
      case 'isExternalCustomer':
        this.params.filter.isExternalCustomer = value;
        if (value === CustomerFilterType.WITHOUT_RFC) {
          this.params.filter.customerId = null;
        }
        break;
    }
  }

  openModalConfirmation(quote: any) {
    this.idQuote = quote.id;
    this.subtitle = `¿Confirma la eliminación de la cotización "${quote.quoteName}"?`;
    this.modalService.component = this.template;
    this.modalService.openModal();
  }

  resetFilters() {
    this.params.filter = {
      page: 1,
      pageSize: this.pageSize,
      me: this.params.filter.me,
      customerName: null,
      userId: null,
      quoteName: null,
      status: null,
      from: null,
      to: null,
      customerId: null,
      isExternalCustomer: null,
    };
    this.reset = !this.reset;
    this.getQuotes();
  }

  showCustomerName(customer: any) {
    return Util.showInsuranceVehicle(customer);
  }

  getOwners() {
    this.loading.track(this.quoteService.getOwners()).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (response) => {
        this.owners = response.data.map(
          (owner: { name: string; id: number }) => ({
            label: owner.name,
            value: owner.id,
          })
        );
      },
      error: (error) => {
        this.showNotificationError('Error al obtener propietarios');
      },
    });
  }

  onChangeOwners() {
    if (this.selectedOwner) {
      this.params.filter.userId = this.selectedOwner;
      this.search();
    } else {
      this.params.filter.userId = null;
      this.search();
    }
  }

  openModalConfirmationToDuplicateOpenQuote(quote: any) {
    this.title = 'Duplicar cotización';
    this.btn2 = 'Duplicar';
    this.idQuote = quote.id;
    this.subtitle = `¿Confirma el duplicado de la cotización "${quote.quoteName}"?`;
    this.modalService.component = this.templateDuplicateQuote();
    this.modalService.openModal();
  }

  getAllClientsUrl(): string {
    return `${environment.API_CLIENTS_SERVICE}${CLIENT_SERVICE.CLIENTS_SEARCH_ALL}`;
  }

  handleSearchClient(client: any) {
    const mode = this.params.filter.isExternalCustomer;
    // Cotizaciones sin RFC no enlazan customerId de catálogo; no enviar id al API.
    if (mode === CustomerFilterType.WITHOUT_RFC) {
      this.params.filter.customerId = null;
      this.reset = false;
      return;
    }
    this.params.filter.customerId = client?.id ?? null;
    this.reset = false;
  }

  handleTextChange(text: string) {
    this.params.filter.customerName = !text ? null : text;
    // Cualquier edición manual invalida la selección del catálogo (evita customerId obsoleto).
    this.params.filter.customerId = null;
    this.reset = false;
  }
}
